/**
 * DOCUMENTATION:
 * Component: SystemDesignFundamentals
 * Purpose: Introduction to Scalability, Latency, and Throughput.
 * * * Note for Junior Developers:
 * - Vertical Scaling (Scale Up): Adding more power (CPU, RAM) to an existing server.
 * - Horizontal Scaling (Scale Out): Adding more servers to the pool.
 * - Latency: How long it takes for a single request to be processed (measured in ms).
 * - Throughput: How many requests can be processed in a given time (e.g., Requests Per Second).
 */

import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
} from "@mui/material";

import StorageIcon from '@mui/icons-material/Storage';
import SpeedIcon from '@mui/icons-material/Speed';
import DnsIcon from '@mui/icons-material/Dns';

const bgImage = "https://images.unsplash.com/photo-1451187580459-43490279c0fa";

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(10, 5, 25, 0.9), rgba(10, 5, 25, 0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(20, 15, 45, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "white",
}));

const MetricBox = styled(Box)(({ theme, color }) => ({
    padding: theme.spacing(2.5),
    backgroundColor: "rgba(255,255,255,0.03)",
    borderRadius: "12px",
    borderBottom: `4px solid \${color}`,
    height: "100%",
}));

export default function SystemDesignFundamentals() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const metrics = [
        {
            title: "Latency",
            icon: <SpeedIcon />,
            color: "#ff6b6b",
            desc: "The time delay between a user's action and the system's response. Low latency is critical for user experience."
        },
        {
            title: "Throughput",
            icon: <DnsIcon />,
            color: "#00c8ff",
            desc: "The volume of work a system can perform in a given time. High throughput is critical for data processing."
        },
        {
            title: "Availability",
            icon: <StorageIcon />,
            color: "#4ade80",
            desc: "The percentage of time the system is operational (e.g., 'Five Nines' or 99.999%)."
        }
    ];

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "#00c8ff" }}>
                    SCALABILITY FUNDAMENTALS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="#00c8ff" mb={2}>
                            Vertical vs. Horizontal Scaling
                        </Typography>
                        <Typography mb={4} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            When your application becomes popular, your single server will eventually run out of resources. You have two choices:
                        </Typography><Grid container spacing={3} mt={2}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 3, bgcolor: "rgba(255,107,107,0.05)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: "12px" }}>
                                <Typography variant="h6" color="#ff6b6b" fontWeight={700} gutterBottom>Vertical (Scale UP)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    Buying a bigger machine. More CPU, more RAM.
                                    <strong> Pros:</strong> Easy to implement, no code changes.
                                    <strong> Cons:</strong> There is a hardware limit (ceiling), and it requires downtime to upgrade.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 3, bgcolor: "rgba(0,200,255,0.05)", border: "1px solid rgba(0,200,255,0.2)", borderRadius: "12px" }}>
                                <Typography variant="h6" color="#00c8ff" fontWeight={700} gutterBottom>Horizontal (Scale OUT)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    Adding more small machines.
                                    <strong> Pros:</strong> Theoretically infinite scaling, higher availability (if one fails, others keep working).
                                    <strong> Cons:</strong> Requires a Load Balancer and more complex software architecture.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    </SectionCard>
                </Grid>

                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="#00c8ff" mb={3}>Key Performance Metrics</Typography>
                        <Grid container spacing={3}>
                            {metrics.map((m, i) => (
                                <Grid item xs={12} md={4} key={i}>
                                    <MetricBox color={m.color}>
                                        <Avatar sx={{ bgcolor: `${m.color}22`, color: m.color, mb: 2 }}>{m.icon}</Avatar>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>{m.title}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>{m.desc}</Typography>
                                    </MetricBox>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/system-design-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#00c8ff", color: "#0a0519", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
